import { HatElement } from '@/components/hat-element.js';
import { eventsClient, type NotificationResponse } from '@/clients/events-client.js';
import { profileClient } from '@/clients/profile-client.js';
import { authService } from '@/services/auth.js';

class HatAdminTesting extends HatElement {
    connectedCallback() {
        super.connectedCallback();
        this._render();
        this._wireEvents();
    }

    _render() {
        this.root.innerHTML = `
            <style>
                h2 {
                    color: white;
                    font-size: 1.4rem;
                    margin: 0 0 24px;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
                }

                .card {
                    background: white;
                    border-radius: 12px;
                    padding: 24px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    max-width: 560px;
                }

                .card h3 {
                    margin: 0 0 8px;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #111;
                }

                .card p {
                    margin: 0 0 20px;
                    font-size: 0.88rem;
                    color: #555;
                }

                button.primary {
                    background: #2563eb;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 20px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    cursor: pointer;
                }

                button.primary:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .result {
                    margin-top: 20px;
                    padding: 16px;
                    border-radius: 8px;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    font-size: 0.88rem;
                    display: none;
                }

                .result.visible { display: block; }

                .result-row {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                    margin-bottom: 8px;
                }

                .result-row:last-child { margin-bottom: 0; }

                .label {
                    color: #666;
                    min-width: 110px;
                    font-size: 0.82rem;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                }

                .value {
                    font-family: monospace;
                    color: #111;
                    word-break: break-all;
                }

                .badge {
                    display: inline-block;
                    padding: 2px 10px;
                    border-radius: 12px;
                    font-size: 0.78rem;
                    font-weight: 600;
                }

                .badge-QUEUED     { background: #dbeafe; color: #1e40af; }
                .badge-DELIVERED  { background: #d1fae5; color: #065f46; }
                .badge-SUPPRESSED { background: #fef9c3; color: #854d0e; }
                .badge-ERROR      { background: #fee2e2; color: #991b1b; }
                .badge-DELAYED    { background: #f3e8ff; color: #6b21a8; }

                .error-msg {
                    color: #991b1b;
                    font-size: 0.88rem;
                    margin-top: 16px;
                    display: none;
                }

                .error-msg.visible { display: block; }
            </style>

            <h2>Testing</h2>

            <div class="card">
                <h3>Test Email</h3>
                <p>Sends a test email to your account using the current user profile.</p>
                <button class="primary" id="send-btn">Send Test Email</button>
                <div class="error-msg" id="error-msg"></div>
                <div class="result" id="result">
                    <div class="result-row">
                        <span class="label">Request ID</span>
                        <span class="value" id="request-id">—</span>
                    </div>
                    <div class="result-row">
                        <span class="label">Notification ID</span>
                        <span class="value" id="notification-id">—</span>
                    </div>
                    <div class="result-row">
                        <span class="label">Status</span>
                        <span id="status">—</span>
                    </div>
                </div>
            </div>
        `;
    }

    _wireEvents() {
        const btn = this.root.getElementById('send-btn') as HTMLButtonElement;
        btn.addEventListener('click', () => this._sendTestEmail());
    }

    async _sendTestEmail() {
        const btn = this.root.getElementById('send-btn') as HTMLButtonElement;
        const errorEl = this.root.getElementById('error-msg')!;
        const resultEl = this.root.getElementById('result')!;

        btn.disabled = true;
        btn.textContent = 'Sending…';
        errorEl.classList.remove('visible');
        resultEl.classList.remove('visible');

        try {
            const profile = await profileClient.getProfile();
            const userId = profile.userId;
            const userName = profile.userName ?? authService.getUserName() ?? 'Unknown';
            const timestamp = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');

            const resp: NotificationResponse = await eventsClient.sendNotifications({
                template: 'test',
                messages: [{
                    userId,
                    variables: { UserName: userName, Timestamp: timestamp }
                }]
            });

            const first = resp.results[0];
            this.root.getElementById('request-id')!.textContent = resp.requestId;
            this.root.getElementById('notification-id')!.textContent = first?.notificationId ?? '—';

            const statusEl = this.root.getElementById('status')!;
            const status = first?.status ?? '—';
            statusEl.innerHTML = `<span class="badge badge-${status}">${status}</span>`;

            resultEl.classList.add('visible');
        } catch (err) {
            errorEl.textContent = err instanceof Error ? err.message : 'An unexpected error occurred.';
            errorEl.classList.add('visible');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Send Test Email';
        }
    }
}

customElements.define('hat-admin-testing', HatAdminTesting);